import _ from 'lodash';
import { IPick, IPlayer } from '@/store/modules/game/interfaces';
import dayjs from 'dayjs'
import { IDraft, IDraftPick } from '@/store/modules/draft/interfaces';
import { ILocationUpdate } from '@/store/modules/cart/interfaces';

export function GetPlayerPosition(player: IPlayer): string {
  return player.position ? player.position : ''
}

export function Multipliers(): number[] {
  return [0, 0, 5, 10, 18, 35, 70, 125, 250, 500];
}

export function GetMultiplier(idx: number): number {
  return Multipliers()[idx]
}

export function GetVisualMultiplier(idx: number): string | undefined {
  const m = GetMultiplier(idx)
  if (m) {
    return `x${m}`
  }
}

export function GetBuyMoreResponseFromCode(code: number, sport: string): string {
  const lookup: {[s: string]: string} = {
    '-2': `We're unable to generate custom matchups for ${sport.toUpperCase()} right now, please try again later`,
  }

  return lookup[code.toString()] || lookup['-2']
}

export function GetSeasonFromYear(year: string): string {
  const lookup: {[s: string]: string} = {
    '2000': '00-01',
    '2001': '01-02',
    '2002': '02-03',
    '2003': '03-04',
    '2004': '04-05',
    '2005': '05-06',
    '2006': '06-07',
    '2007': '07-08',
    '2008': '08-09',
    '2009': '09-10',
    '2010': '10-11',
    '2011': '11-12',
    '2012': '12-13',
    '2013': '13-14',
    '2014': '14-15',
    '2015': '15-16',
    '2016': '16-17',
    '2017': '17-18',
    '2018': '18-19',
    '2019': '19-20',
  }

  return lookup[year]
}

export function GetOUFantasyAspect (source: IPick): string {
  if (!source) { return 'h2h' }

  return source.player.aspect.id.toLowerCase() === 'ou' ? 'ou' : 'h2h'
}

export function GetOUFantasyProjection(pick: IPick) {
  const fvSet = _.get(pick.player, 'seasonFeatureValueSet.featureValues', {})
  const proj = _.find(fvSet, (s) => { return s.name.toLowerCase() === 'ou' })

  if(proj) return proj.value
  if (pick.player.score) return pick.player.score
  return '-'
}

export function FormatEventName(pick: IPick) {
  if (pick.player.aspect.id.toLowerCase() === 'nascar' || pick.player.aspect.id.toLowerCase() === 'ufc') {
    return pick.event.shortName.split('@')[0]
  }
  return pick.event.shortName
}

export function FormatEventDate(pick: IPick) {
  return FormatDate(pick.event.startTime)
}

export function FormatDate(time: number) {
  return dayjs(time).format('h:mm A, D MMM')
}

export function FormatCurrency(currency: number): string {
  return '$' + currency.toFixed(2)
}

export function StatusFormat(status: string): string {
  if (status === 'live' || status === 'queued') {
    return 'Live'
  }

  if (status.indexOf('winner') > -1) {
    return 'Won'
  }

  if (status.indexOf('loser') > -1) {
    return 'Lost'
  }

  if (status.indexOf('rejected') > -1) {
    return 'Rejected'
  }

  if (status.indexOf('open') > -1) {
    return 'Open'
  }

  if (status.indexOf('void') > -1) {
    return 'Void'
  }

  return 'Pending'
}

export function GetWinningPicks(draft: IDraft) {
  var winners = _.filter(draft.json.picks, function(p) {
    return p.status === 'winner';
  });

  return winners.length + '/' + UnVoidedPicks(draft.json.picks).length;
}

export function UnVoidedPicks(picks: IDraftPick[]) {
  return _.filter(picks, function(p) {
    return p.status !== 'void';
  })
}

export function StripTheGarbage(evt: KeyboardEvent) {
  if (evt.keyCode < 48 && evt.keyCode !== 46 || evt.keyCode > 59) {
    evt.preventDefault()
  }
}

export function FormatDollars(userAmountVis: string): number {
  if (userAmountVis != '') {
    let num: number = parseFloat(userAmountVis)

    let countDecimals = function (value: number) {
      if(Math.floor(value) === value) return 0
      return value.toString().split('.')[1].length || 0
    }
    
    let decimal = countDecimals(num);

    if (decimal < 2) {
      userAmountVis = num.toFixed(2)
    }
    
    if (decimal > 2) {
      userAmountVis = num.toFixed(decimal)
    }
    
    if (num < 1) {
      userAmountVis = '.' + String(num).split('.')[1]
    }

    return parseFloat(userAmountVis)
  }
  return 0
}

export async function GetLocation(): Promise<Position> {
  return new Promise<Position>((resolve, reject) => {
    if(!('geolocation' in navigator)) {
      reject(new Error('Geolocation is not available'));
    }
    navigator.geolocation.getCurrentPosition(pos => {
      resolve(pos);
    }, err => {
      const res = new Error(err.message)
      reject(res);
    });
  });
}

export async function GetGeoData(): Promise<ILocationUpdate | null> {
  return await GetLocation().then( (position: Position) => {
    const res = {
      altitude: position.coords.altitude, 
      latitude: position.coords.latitude, 
      longitude: position.coords.longitude, 
      accuracy: position.coords.accuracy, 
      speed: position.coords.accuracy, 
      timestamp: position.timestamp
    } as ILocationUpdate
    return res
  }).catch( (err) => {
    //@ts-ignore
    Sentry.captureException(`${err}`);
    return null
  })
}

export function GetUrlParameter(name: string) {
  name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
  const results = regex.exec(location.hash);
  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
}

export function ErrorMessageLookup(): {[s: number]: string} {
  return {
    0: 'There has been a problem with your request, please contact support',
    1: `Withdrawals aren't currently supported in your geographic location`,
    10: `Your balance is insufficient to withdraw the requested amount`,
    11: `You still have rollover balance to use before you can withdraw`,
    12: `We're currently unable to create a request with the payment processor. Please try again later`,
    13: `Your account could not be validated. Please logout and try again`,
    15: `Invalid withdrawal amount, please try again`
  }
}